import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import api from "../../lib/api";
import { fmtDate, fmtDateTime, inr, inrFull } from "../../lib/format";
import { Empty } from "../kit";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { PayDialog } from "./GmsForms";

export const PAY_UI = {
  overdue: ["Overdue", "bg-red-50 text-red-700 border-red-200"],
  due_today: ["Due Today", "bg-amber-50 text-amber-800 border-amber-200"],
  upcoming: ["Upcoming", "bg-sky-50 text-sky-700 border-sky-200"],
  paid: ["Paid", "bg-green-50 text-green-700 border-green-200"],
};

export const PayBadge = ({ s, testid }) => <span className={`text-xs font-semibold border rounded-full px-2.5 py-0.5 whitespace-nowrap ${PAY_UI[s][1]}`} data-testid={testid}>{PAY_UI[s][0]}</span>;

const daysText = (d) => (d === 0 ? "Today" : d > 0 ? `in ${d} day${d > 1 ? "s" : ""}` : `${-d} day${d < -1 ? "s" : ""} late`);

function PaymentCard({ p, showStaff, onPay, onOpen }) {
  return (
    <div className={`bs-card p-4 ${p.state === "overdue" ? "border-red-200" : ""}`} data-testid={`gms-payment-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button className="text-left" onClick={() => onOpen(p.plan_id)} data-testid={`gms-payment-open-${p.id}`}>
          <div className="font-semibold text-[#011E38]">{p.customer_name}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {p.mobile} · {p.plan_ref}{showStaff && ` · ${p.staff_name}`}</div>
        </button>
        <PayBadge s={p.state} testid={`gms-payment-status-${p.id}`} />
      </div>
      <div className="flex items-end justify-between mt-3">
        <div>
          <div className="num text-2xl font-bold text-[#011E38]">{inrFull(p.state === "paid" ? p.paid_amount : p.amount)}</div>
          <div className="text-xs text-slate-500">Payment #{p.number} · {p.state === "paid" ? `paid ${fmtDate(p.paid_date)}` : `due ${fmtDate(p.due_date)} · ${daysText(p.days_remaining)}`}</div>
        </div>
        {p.state !== "paid" && <Button size="sm" onClick={() => onPay(p)} className="bg-[#011E38] hover:bg-[#032C52]" data-testid={`gms-mark-paid-${p.id}`}>Mark PAID</Button>}
      </div>
    </div>
  );
}

export function PlanSchedule({ planId, onClose }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/gms/plans/${planId}`).then((r) => setP(r.data)); }, [planId]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg" data-testid="gms-plan-schedule">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#011E38]">{p ? `${p.plan_ref} · ${p.customer_name}` : "Loading…"}</DialogTitle></DialogHeader>
        {p && (
          <>
            <div className="text-sm text-slate-600">{p.mobile} · {p.staff_name} · {inrFull(p.monthly_amount)} × {p.instalments} from {fmtDate(p.start_date)}</div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500"><tr><th className="py-2 text-left">#</th><th className="text-left">Due</th><th className="text-right">Amount</th><th className="text-right">Status</th></tr></thead>
              <tbody>
                {p.payments.map((x) => (
                  <tr key={x.id} className="border-t border-slate-100" data-testid={`gms-schedule-row-${x.number}`}>
                    <td className="py-2.5 font-semibold">{x.number}</td>
                    <td>{fmtDate(x.due_date)}{x.paid_date && <div className="text-xs text-slate-400" title={x.updated_at && fmtDateTime(x.updated_at)}>paid {fmtDate(x.paid_date)} by {x.updated_by}</div>}</td>
                    <td className="text-right num">{inr(x.paid_amount ?? x.amount)}</td>
                    <td className="text-right"><PayBadge s={x.state} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function GmsPayments({ filters, showStaff, reloadKey, onChanged }) {
  const [view, setView] = useState("open");
  const [d, setD] = useState(null);
  const [pay, setPay] = useState(null);
  const [plan, setPlan] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/gms/payments", { params: { view, ...filters } }).then((r) => setD(r.data)); }, [view, filters, reloadKey, tick]);
  return (
    <section data-testid="gms-payments">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-slate-500">{view === "open" ? `Overdue, due today and next ${d?.window_days ?? 30} days · nearest first` : "Recently paid"}</div>
        <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold">
          {[["open", "Upcoming"], ["paid", "Paid"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-full ${view === v ? "bg-[#011E38] text-white" : "text-slate-500"}`} data-testid={`gms-payments-view-${v}`}>{l}</button>
          ))}
        </div>
      </div>
      {d && !d.rows.length && <Empty>{view === "open" ? "No GMS payments coming up." : "No payments marked paid yet."}</Empty>}
      <div className="grid md:grid-cols-2 gap-3">
        {d?.rows.map((p) => <PaymentCard key={p.id} p={p} showStaff={showStaff} onPay={setPay} onOpen={setPlan} />)}
      </div>
      {pay && <PayDialog payment={pay} onClose={() => setPay(null)} onDone={() => { setPay(null); setTick((t) => t + 1); onChanged?.(); }} />}
      {plan && <PlanSchedule planId={plan} onClose={() => setPlan(null)} />}
    </section>
  );
}
